import { Box, Card, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import { push } from 'connected-react-router'
import React from 'react'
import { connect } from 'react-redux'

import {
  cancelEditingOrder,
  loadEditOrder,
  removeItemFromOrderCartStart,
} from '../../redux/modules/cart'
import { clearMenuItem } from '../../redux/modules/order'
import {
  selectCurrentLocationWithAddress,
  selectMenuItemForEdit,
} from '../../redux/modules/selectors'
import { checkoutRoute } from '../../routes/routes'
import { formatUrl } from '../../util/formatUtils'
import { pushSnowplow, toOrderProcessSchema } from '../../util/snowplow'
import { OrderCart } from '../cart/Cart'
import DeliveryDetails from '../common/DeliveryDetails'
import MapPreview from '../common/MapPreview'
import StickyContainer from '../common/StickyContainer'
import PersonalizeOrder from '../personalize-order/PersonalizeOrder'
import ButtonCheckout from './ButtonCheckout'

const useStyles = makeStyles({
  card: {
    padding: '0',
  },
  orderInfoText: {
    fontWeight: 'bold',
  },
})

export const OrderSidebar = ({
  cancelEditingOrder,
  clearMenuItem,
  deliveryDropoff,
  isAuthenticated,
  isDeliveryDropoffLoading,
  isEditingOrder,
  isEditingPastOrder,
  isCartSaving,
  isLoadingOrderForEdit,
  isLocationLoading,
  isOrderCartLoading,
  loadEditOrder,
  location,
  menuItemForEdit,
  normalizedMenuItem,
  orderCart,
  orderCartError,
  push,
  removeItemFromOrderCartStart,
}) => {
  const classes = useStyles()

  const handleClose = () => {
    clearMenuItem()
    cancelEditingOrder()
  }

  const disableCheckout = !isAuthenticated || orderCart.orderItems.length === 0
  const isLoading = isLocationLoading || isDeliveryDropoffLoading || isOrderCartLoading

  return (
    <StickyContainer bottomOffset={18} stickyStyle={{ paddingTop: '100px' }} topOffset={-100}>
      <Card className={classes.card}>
        {isLoading && <SkeletonLoading />}

        {!isLoading && location?.address && (
          <MapPreview
            address={`${location.address.street} ${location.address.city} ${location.address.state} ${location.address.zip}`}
          />
        )}

        {!isLoading && (
          <Box padding={3}>
            <DeliveryDetails location={location} deliveryDropoff={deliveryDropoff} />

            <Divider />

            {orderCart && deliveryDropoff && (
              <OrderCart
                cart={orderCart}
                deleteMenuItem={removeItemFromOrderCartStart}
                isCartSaving={isCartSaving}
                dropoffId={deliveryDropoff.dropoffId}
                editable="true"
                editMenuItem={loadEditOrder}
                isLoadingOrderForEdit={isLoadingOrderForEdit}
                isEditingPastOrder={false}
                showEditLinks="true"
              />
            )}
          </Box>
        )}
      </Card>

      <ButtonCheckout
        disabled={disableCheckout}
        error={orderCartError}
        isContinue
        onClick={() => {
          pushSnowplow(toOrderProcessSchema('daily', 'move_to_checkout', 'cart'))
          push(
            formatUrl(checkoutRoute.path, {
              dropoffId: deliveryDropoff.dropoffId,
              locationId: location.deliveryLocationId,
            }),
          )
        }}
      />

      {!isEditingPastOrder && menuItemForEdit && isEditingOrder && (
        <PersonalizeOrder
          dropoffId={deliveryDropoff.dropoffId}
          handleClose={handleClose}
          isEditingFromCheckout={false}
          isEditingFromMenu="true"
          isEditingOrder={isEditingOrder}
          isEditingPastOrder={false}
          locationId={location.deliveryLocationId}
          menuItemForEdit={menuItemForEdit}
          menuItemId={menuItemForEdit.menuItemId}
          normalizedMenuItem={normalizedMenuItem}
        />
      )}
    </StickyContainer>
  )
}

const SkeletonLoading = () => (
  <>
    <Skeleton height={150} variant="rect" />
    <Box padding={3}>
      <Skeleton height={30} width="80%" />
      <Skeleton height={30} width="80%" />
      <Box marginY={2}>
        <Skeleton height={40} width="90%" />
      </Box>
      <Skeleton height={50} />
    </Box>
  </>
)

const mapStateToProps = state => {
  const { isOrderCartLoading, orderCart, orderCartError } = state.cart
  const { isAuthenticated } = state.user
  const { normalizedMenuItem } = state.personalize
  const { deliveryDropoff, isDeliveryDropoffLoading } = state.delivery
  const location = selectCurrentLocationWithAddress(state)
  const menuItemForEdit = selectMenuItemForEdit(state)
  const { isLocationLoading } = state.location
  const { isEditingOrder, isEditingPastOrder, isCartSaving, isLoadingOrderForEdit } = state.cart
  return {
    deliveryDropoff,
    isAuthenticated,
    isDeliveryDropoffLoading,
    isEditingOrder,
    isEditingPastOrder,
    isCartSaving,
    isLoadingOrderForEdit,
    isLocationLoading,
    isOrderCartLoading,
    location,
    menuItemForEdit,
    normalizedMenuItem,
    orderCart,
    orderCartError,
  }
}

const mapDispatchToProps = {
  cancelEditingOrder,
  clearMenuItem,
  loadEditOrder,
  push,
  removeItemFromOrderCartStart,
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderSidebar)
